import React, { useState, useRef } from "react";
import { useEffectOnce, useInput } from "@lib-shared/hooks";
import { sessionManager } from "core/instances";
import { Button, ValidationMessage } from "./common-ui";

type CaptureUserDetailsFormProps = {
    onSubmit: any,
    onCancel?: any,
    onClose?: any,
    isClosable: boolean,
    canCancel?: boolean,
    display?: boolean,
    title?: string,
    cssClassName?: string
}

const CaptureUserDetailsForm = (props: CaptureUserDetailsFormProps) => {
    const {
        onSubmit,
        onCancel,
        onClose,
        isClosable = true,
        display = true,
        canCancel = true,
        title = "Please enter your name:",
        cssClassName
    } = props;
    const dialog = useRef<any>(null);

    const [isSaving, setIsSaving] = useState(false);

    const inputValidation = (value: string) => value.trim().length > 0;

    const inputOptions = (id: string, label: string, initialValue = "") => {
        return { id, label, initialValue };
    }

    const profile = sessionManager.getProfile();

    const firstNameInput = useInput(inputValidation, inputOptions("firstName", "Name", profile.firstName));

    const lastNameInput = useInput(inputValidation, inputOptions("lastName", "Surname", profile.lastName));

    const getClassName = (input: HTMLInputElement) => input.value.length > 0 ? "hasvalue" : "";
    const firstNameLabelCssClassName = getClassName(firstNameInput);
    const lastNameLabelCssClassName = getClassName(lastNameInput);

    const cancel = (e: any) => {
        if (onCancel == null) {
            return;
        }

        onCancel();
    }

    const submit = async () => {
        if (onSubmit == null) {
            return;
        }

        setIsSaving(true);

        await onSubmit(firstNameInput.value, lastNameInput.value);

        setIsSaving(false);
    }

    const isSubmitDisabled = firstNameInput.hasError
        || lastNameInput.hasError
        || isSaving
        || !firstNameLabelCssClassName.length
        || !lastNameLabelCssClassName.length;

    const close = () => {
        dialog.current.close();

        if (onClose == null) {
            return;
        }

        onClose();
    }

    useEffectOnce(() => {
        setTimeout(() => {
            //workaround for bug. see: https://github.com/facebook/react/issues/24399
            dialog.current.removeAttribute("open");
            dialog.current.showModal();
        }, 500);
    });

    if (!display) {
        return null;
    }

    const className = `floaty-form modal-form${cssClassName ? ` ${cssClassName}` : ""}`

    return (
        <dialog ref={dialog} id="capture-user-details" className={className}>
            <form method="dialog">
                {isClosable ? (
                    <button type="button" className="close" onClick={close}>
                        <i className="icon-close close"></i>
                    </button>
                ) : null}

                <div className="row title-row" dangerouslySetInnerHTML={{ __html: title }}>
                </div>

                <div className="row two-cols">
                    <div className="col">
                        <input
                            type="text"
                            placeholder={firstNameInput.label}
                            id="firstName"
                            name="firstName"
                            value={firstNameInput.value}
                            onChange={firstNameInput.valueChangeHandler}
                            onBlur={firstNameInput.inputBlurHandler}
                        />

                        <label htmlFor="firstName">{firstNameInput.label}</label>

                        <ValidationMessage
                            valid={!firstNameInput.hasError}
                            message="Please enter your name"
                        />
                    </div>

                    <div className="col">
                        <input
                            type="text"
                            placeholder={lastNameInput.label}
                            id="lastName"
                            name="lastName"
                            value={lastNameInput.value}
                            onChange={lastNameInput.valueChangeHandler}
                            onBlur={lastNameInput.inputBlurHandler}
                        />

                        <label htmlFor="firstName">{lastNameInput.label}</label>

                        <ValidationMessage
                            valid={!lastNameInput.hasError}
                            message="Please enter your surname"
                        />
                    </div>
                </div>

                <div className="row col modal-buttons-row">
                    {canCancel && <Button
                        colour="outline-black"
                        onClick={cancel}
                        type="button"
                        text="Cancel" />}
                    <Button
                        disabled={isSubmitDisabled}
                        onClick={submit}
                        text="Save" />
                </div>
            </form>
        </dialog>
    );
}

export default CaptureUserDetailsForm;
